import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Card, 
  Row, 
  Col, 
  Statistic, 
  DatePicker, 
  Select, 
  Input, 
  Button, 
  Space, 
  Tag, 
  Tooltip,
  message,
  Modal,
  Descriptions,
  Badge,
  Divider
} from 'antd';
import { 
  SearchOutlined,
  EyeOutlined,
  DeleteOutlined,
  ReloadOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';

const { RangePicker } = DatePicker;
const { Option } = Select;

function RequestLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    start_date: null,
    end_date: null,
    user_id: null,
    ip_address: '',
    method: '',
    endpoint: '',
    response_status: null
  });
  
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0
  });

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [pagination.current, pagination.pageSize]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (pagination.current - 1) * pagination.pageSize,
        limit: pagination.pageSize,
        ...filters
      };
      
      // Convert dates to string format
      if (filters.start_date) {
        params.start_date = filters.start_date.format('YYYY-MM-DD');
      }
      if (filters.end_date) {
        params.end_date = filters.end_date.format('YYYY-MM-DD');
      }
      
      const response = await axios.get('/api/logs/', { params });
      const { data, total, page, pageSize } = response.data;
      
      setLogs(data);
      
      // Update pagination with correct total count
      setPagination(prev => ({ 
        ...prev, 
        total: total,
        current: page,
        pageSize: pageSize
      }));
      
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      message.error('Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await axios.get('/api/logs/stats?days=7');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchLogs();
  };


  const handleViewLog = async (logId) => {
    try {
      const response = await axios.get(`/api/logs/${logId}`);
      setSelectedLog(response.data);
      setModalVisible(true);
    } catch (error) {
      message.error('Failed to fetch log details');
    }
  };

  const handleClearAllLogs = async () => {
    Modal.confirm({
      title: 'Clear All Logs',
      content: 'Are you sure you want to delete ALL logs? This action cannot be undone.',
      okText: 'Yes, Clear All',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await axios.delete('/api/logs/all');
          message.success('All logs cleared successfully');
          fetchLogs();
          fetchStats();
        } catch (error) {
          message.error('Failed to clear all logs');
        }
      }
    });
  };

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'green';
    if (status >= 300 && status < 400) return 'blue';
    if (status >= 400 && status < 500) return 'orange';
    return 'red';
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return 'blue';
      case 'POST': return 'green';
      case 'PUT': return 'orange';
      case 'DELETE': return 'red';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Time',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => moment(date).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      width: 100,
      render: (method) => (
        <Tag color={getMethodColor(method)}>{method}</Tag>
      ),
    },
    {
      title: 'Endpoint',
      dataIndex: 'endpoint',
      key: 'endpoint',
      ellipsis: true,
      render: (endpoint) => (
        <Tooltip title={endpoint}>
          {endpoint}
        </Tooltip>
      ),
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 120,
    },
    {
      title: 'User',
      dataIndex: 'username',
      key: 'username',
      width: 100,
      render: (username) => username || 'Not logged in',
    },
    {
      title: 'Status',
      dataIndex: 'response_status',
      key: 'response_status',
      width: 80,
      render: (status) => (
        <Badge 
          color={getStatusColor(status)} 
          text={status} 
        />
      ),
    },
    {
      title: 'Response',
      dataIndex: 'response_time',
      key: 'response_time',
      width: 120,
      render: (time) => time ? `${Math.round(time)}ms` : '-',
    },
    {
      title: 'Action',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button
          type="link" 
          icon={<EyeOutlined />} 
          onClick={() => handleViewLog(record.id)}
        />
      ),
    },
  ];

  return (
    <div style={{ 
      padding: '24px',
      background: '#f5f5f5',
      minHeight: '100vh'
    }}>
      <Row gutter={[16, 16]}>
        {/* Stats Cards */}
        <Col span={24}>
          <Card 
            title="Request Statistics (Last 7 Days)" 
            loading={statsLoading}
            size="small"
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8} md={8}>
                <Statistic 
                  title="Total Requests" 
                  value={stats.total_requests || 0}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col xs={24} sm={8} md={8}>
                <Statistic 
                  title="Avg Response Time" 
                  value={stats.avg_response_time || 0} 
                  suffix="ms"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col xs={24} sm={8} md={8}>
                <Statistic 
                  title="Success Rate" 
                  value={Math.round(((stats.status_stats?.find(s => s.status >= 200 && s.status < 300)?.count || 0) / (stats.total_requests || 1)) * 100)} 
                  suffix="%"
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
            </Row>
            <Row style={{ marginTop: '16px' }}>
              <Col span={24} style={{ textAlign: 'center' }}>
                <Button 
                  type="primary" 
                  icon={<BarChartOutlined />}
                  onClick={fetchStats}
                >
                  Refresh Stats
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Filters */}
        <Col span={24}>
          <Card title="Filters" size="small">
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={12} md={6}>
                <RangePicker
                  placeholder={['Start Date', 'End Date']}
                  value={[filters.start_date, filters.end_date]}
                  onChange={(dates) => {
                    setFilters(prev => ({
                      ...prev,
                      start_date: dates?.[0] || null,
                      end_date: dates?.[1] || null
                    }));
                  }}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Input
                  placeholder="IP Address"
                  value={filters.ip_address}
                  onChange={(e) => handleFilterChange('ip_address', e.target.value)}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder="Method"
                  value={filters.method}
                  onChange={(value) => handleFilterChange('method', value)}
                  allowClear
                  style={{ width: '100%' }}
                >
                  <Option value="GET">GET</Option>
                  <Option value="POST">POST</Option>
                  <Option value="PUT">PUT</Option>
                  <Option value="DELETE">DELETE</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Input
                  placeholder="Endpoint"
                  value={filters.endpoint}
                  onChange={(e) => handleFilterChange('endpoint', e.target.value)}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={12} md={3}>
                <Input
                  placeholder="Status"
                  type="number"
                  value={filters.response_status}
                  onChange={(e) => handleFilterChange('response_status', e.target.value ? parseInt(e.target.value) : null)}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={12} md={3}>
                <Button 
                  type="primary" 
                  icon={<SearchOutlined />} 
                  onClick={handleSearch}
                  style={{ width: '100%' }}
                >
                  Search
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Logs Table */}
        <Col span={24}>
          <Card 
            title={`Request Logs (${pagination.total} total)`}
            size="small"
            extra={
              <Space>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={fetchLogs}
                >
                  Refresh
                </Button>
                <Button 
                  danger 
                  type="primary"
                  icon={<DeleteOutlined />} 
                  onClick={handleClearAllLogs}
                >
                  Clear All Logs
                </Button>
              </Space>
            }
          >
            <Table
              columns={columns}
              dataSource={logs}
              loading={loading}
              rowKey="id"
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                pageSizeOptions: ['10', '20', '50', '100', '200', '500', '1000'],
                onChange: (page, pageSize) => {
                  setPagination(prev => ({ 
                    ...prev, 
                    current: page, 
                    pageSize: pageSize || prev.pageSize 
                  }));
                },
                onShowSizeChange: (current, size) => {
                  setPagination(prev => ({ 
                    ...prev, 
                    current: 1,  // Reset to first page when changing page size
                    pageSize: size 
                  }));
                }
              }}
              scroll={{ x: 1200 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Log Detail Modal */}
      <Modal
        title="Request Log Details"
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedLog && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="ID">{selectedLog.id}</Descriptions.Item>
            <Descriptions.Item label="Time">
              {moment(selectedLog.created_at).format('DD/MM/YYYY HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="Method">
              <Tag color={getMethodColor(selectedLog.method)}>{selectedLog.method}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Endpoint">{selectedLog.endpoint}</Descriptions.Item>
            <Descriptions.Item label="IP Address">{selectedLog.ip_address}</Descriptions.Item>
            <Descriptions.Item label="User">
              {selectedLog.username || 'Not logged in'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge color={getStatusColor(selectedLog.response_status)} text={selectedLog.response_status} />
            </Descriptions.Item>
            <Descriptions.Item label="Response Time">
              {selectedLog.response_time ? `${Math.round(selectedLog.response_time)}ms` : 'N/A'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
}

export default RequestLogs;